<script setup lang="ts">
import { useElementSize, useWindowSize } from '@vueuse/core';
import { mapValues } from 'lodash';
import { computed, provide, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useNotifications } from '../../notifications';
import { useBreakpoints } from '../../utils/breakpoints';
import type { ComputedLayoutSections, LayoutOption, LayoutSections } from '../types/layoutOptions';

performance.mark('app-layout-setup');

const windowSize = useWindowSize();

const props = defineProps<{
  layout: LayoutSections;
}>();

/**
 * Layout Options
 */
const sidebarOpen = ref(false);
const contentElement = ref<HTMLElement | undefined>(undefined);
const contentSize = useElementSize(contentElement);

const breakpoints = useBreakpoints();
const greaterThanMd = breakpoints.greater('md');
const smallerThanLg = breakpoints.smaller('lg');

const options = computed(() => {
  const computedOptions: ComputedLayoutSections<LayoutSections> = mapValues<
    LayoutSections,
    LayoutOption<any>
  >(props.layout, (section) => {
    if (section === false) {
      return {
        show: false,
      };
    }

    return mapValues(section, (option) => (option instanceof Function ? option() : option));
  });

  if (smallerThanLg.value && computedOptions) {
    computedOptions.sidebar.fixed = false;
  }

  if (greaterThanMd.value && computedOptions) {
    computedOptions.navBar.show = false;
  }

  return computedOptions;
});

const router = useRouter();

watch(
  () => router.currentRoute.value,
  () => {
    if (!smallerThanLg.value || !options.value.sidebar.fixed) {
      sidebarOpen.value = false;
    }
  },
  {
    immediate: true,
  },
);

const notifications = useNotifications();

const visibleContentHeight = computed(
  () =>
    `calc(${windowSize.height.value}px - ${
      (!options.value.header.overlay && options.value.header.height) || '0px'
    } - ${options.value.announcement.show && !notifications.empty('announcement') ? options.value.announcement.height : '0px'})`,
);

provide('layoutOptions', options);
provide('layoutToggleSidebar', () => (sidebarOpen.value = !sidebarOpen.value));
provide('contentWidth', () => contentSize.width);
</script>

<template>
  <div
    id="app-layout"
    class="layout"
    :style="{
      '--content-width': `${contentSize.width.value}px`,
      '--visible-content-height': visibleContentHeight,
      '--header-and-announcement-height': `calc(${options.header.height} + ${options.announcement.show && !notifications.empty('announcement') ? options.announcement.height : '0px'})`,
    }"
  >
    <header
      v-if="options.header.show"
      class="header transition-all z-header"
      :class="{
        'sticky top-0': options.header.fixed,
      }"
      :style="{
        // height: options.header.overlay ? '0' : options.header.height,
        height: options.header.overlay ? '0' : 'var(--header-and-announcement-height)',
        // boxShadow: '0px 0px 8px 0 #2224',
      }"
    >
      <div
        v-if="options.announcement.show && !notifications.empty('announcement')"
        :style="{ height: options.announcement.height }"
      >
        <slot name="announcement" v-bind="{ options }" />
      </div>
      <slot name="header" v-bind="{ options }" />
    </header>
    <aside
      v-if="options.sidebar.show"
      class="sidebar z-sidebar fixed left-0 w-0"
      :class="{
        open: options.sidebar.fixed || sidebarOpen,
      }"
      :style="{
        top: 'var(--header-and-announcement-height)',
        bottom: options.navBar.show ? options.navBar.height : '0',
      }"
    >
      <Transition name="fade">
        <div
          v-if="sidebarOpen && !options.sidebar.fixed"
          class="fixed top-0 left-0 right-0 bottom-0 opacity-20 bg-black transition-opacity"
          @click="sidebarOpen = false"
        />
      </Transition>
      <div
        class="slide relative"
        :style="{
          width: options.sidebar.width,
        }"
      >
        <svg
          v-if="options.sidebar.fixed"
          viewBox="0 0 16 16"
          class="absolute top-0 transition-[width] duration-500 w-0"
          :class="{
            'w-[--tml-page-radius]': sidebarOpen || options.sidebar.fixed,
          }"
          :style="{
            left: options.sidebar.width,
          }"
        >
          <mask id="clip">
            <rect x="0" y="0" width="16" height="16" fill="#fff" />
            <circle cx="16" cy="16" r="16" fill="#000" />
          </mask>
          <path
            d="M -2 -2 L 16 -2 L 16 0 L 0 16 L -2 16 Z"
            mask="url(#clip)"
            fill="var(--tml-menu-background-color)"
          />
        </svg>
        <slot name="sidebar" v-bind="{ options }" />
      </div>
    </aside>
    <main
      v-if="options.content.show"
      ref="contentElement"
      class="content flex"
      :style="{
        marginLeft: options.sidebar.fixed ? options.sidebar.width : '',
        minHeight: visibleContentHeight,
      }"
    >
      <div
        class="container grow flex flex-col"
        :style="{
          padding: options.content.width !== 'container' ? '0px' : undefined,
          maxWidth: options.content.width !== 'container' ? options.content.width : undefined,
        }"
      >
        <slot name="content" v-bind="{ options }" />
      </div>
    </main>
    <footer
      v-if="options.footer.show"
      class="footer z-footer"
      :style="{
        marginLeft: options.sidebar.fixed ? options.sidebar.width : '',
      }"
    >
      <slot name="footer" v-bind="{ options }" />
    </footer>
    <footer
      v-if="options.subFooter.show"
      class="subfooter z-subfooter"
      :style="{
        marginLeft: options.sidebar.fixed ? options.sidebar.width : '',
      }"
    >
      <slot name="subfooter" v-bind="{ options }" />
    </footer>
    <nav
      v-if="options.navBar.show"
      :style="{
        height: options.navBar.height,
      }"
    >
      <div class="fixed bottom-0 z-subfooter-nav-bar">
        <slot name="nav-bar" v-bind="{ options }" />
      </div>
    </nav>

    <div class="absolute z-100">
      <slot name="global" v-bind="{ options }" />
    </div>
  </div>
</template>

<style>
body {
  overflow-y: scroll;
  overflow-x: hidden;
}
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.sidebar .slide {
  position: absolute;
  transform: translateX(-100%);
  transition: transform 0.3s;
  height: 100%;
}
.sidebar.open .slide {
  transform: translateX(0%);
}
.sidebar.fixed .slide {
  position: relative;
}
</style>
